import React, { ComponentType, FC } from "react";
import * as Outreach from "../OutreachUi";
import * as HubSpot from "../HubSpotUi";
import { useCrm } from "./useCrm";

/**
 * The settings-page kit in the look of the org's CRM: every part renders the
 * Outreach kit (components/OutreachUi) for Outreach orgs and the HubSpot kit
 * (components/HubSpotUi) for HubSpot orgs. The names and props are the
 * Outreach kit's, so a screen moves over by changing its import.
 *
 * OU and OUTREACH_SANS are for a screen's own styled parts. OU's colours are
 * custom properties that each kit's <OuPage> (and html[data-crm="hubspot"])
 * sets, and OUTREACH_SANS falls back to the Outreach stack unless HubSpot's
 * stylesheet defines --crm-sans, so Outreach orgs render exactly as before.
 */

export { useCrm, useCrmName, useIsHubSpotCrm } from "./useCrm";
export type { Crm } from "./useCrm";

export const OU = Outreach.OU;
export const OUTREACH_SANS = `var(--crm-sans, ${Outreach.OUTREACH_SANS})`;

export type { OuTone, OuSelectOption, OuTableColumn, OuTableProps } from "../OutreachUi";
export { RefreshIcon, SyncIcon, QueueIcon, PhoneIcon, ExternalIcon } from "../OutreachUi";

function crmPart<P extends object>(outreach: ComponentType<P>, hubspot: ComponentType<P>, name: string): FC<P> {
  const Part: FC<P> = (props) => {
    const Kit = useCrm() === "hubspot" ? hubspot : outreach;
    return <Kit {...props} />;
  };
  Part.displayName = `Crm(${name})`;
  return Part;
}

export const OuPage = crmPart(Outreach.OuPage, HubSpot.OuPage, "OuPage");
export const OuCard = crmPart(Outreach.OuCard, HubSpot.OuCard, "OuCard");
export const OuTitle = crmPart(Outreach.OuTitle, HubSpot.OuTitle, "OuTitle");
export const OuSectionTitle = crmPart(Outreach.OuSectionTitle, HubSpot.OuSectionTitle, "OuSectionTitle");
export const OuDescription = crmPart(Outreach.OuDescription, HubSpot.OuDescription, "OuDescription");
export const OuCardHeader = crmPart(Outreach.OuCardHeader, HubSpot.OuCardHeader, "OuCardHeader");
export const OuSettingRow = crmPart(Outreach.OuSettingRow, HubSpot.OuSettingRow, "OuSettingRow");
export const OuFieldLabel = crmPart(Outreach.OuFieldLabel, HubSpot.OuFieldLabel, "OuFieldLabel");
export const OuControl = crmPart(Outreach.OuControl, HubSpot.OuControl, "OuControl");
export const OuField = crmPart(Outreach.OuField, HubSpot.OuField, "OuField");
export const OuSelect = crmPart(Outreach.OuSelect, HubSpot.OuSelect, "OuSelect");
export const OuSwitch = crmPart(Outreach.OuSwitch, HubSpot.OuSwitch, "OuSwitch");
export const OuPrimaryButton = crmPart(Outreach.OuPrimaryButton, HubSpot.OuPrimaryButton, "OuPrimaryButton");
export const OuSecondaryButton = crmPart(Outreach.OuSecondaryButton, HubSpot.OuSecondaryButton, "OuSecondaryButton");
export const OuActions = crmPart(Outreach.OuActions, HubSpot.OuActions, "OuActions");
export const OuCardFooter = crmPart(Outreach.OuCardFooter, HubSpot.OuCardFooter, "OuCardFooter");
export const OuPill = crmPart(Outreach.OuPill, HubSpot.OuPill, "OuPill");
export const OuNotice = crmPart(Outreach.OuNotice, HubSpot.OuNotice, "OuNotice");
export const OuEmptyState = crmPart(Outreach.OuEmptyState, HubSpot.OuEmptyState, "OuEmptyState");
export const OuBone = crmPart(Outreach.OuBone, HubSpot.OuBone, "OuBone");
export const OuSearch = crmPart(Outreach.OuSearch, HubSpot.OuSearch, "OuSearch");
export const OuIconButton = crmPart(Outreach.OuIconButton, HubSpot.OuIconButton, "OuIconButton");
export const OuCheckbox = crmPart(Outreach.OuCheckbox, HubSpot.OuCheckbox, "OuCheckbox");
export const OuResultCount = crmPart(Outreach.OuResultCount, HubSpot.OuResultCount, "OuResultCount");
export const OuTooltip = crmPart(Outreach.OuTooltip, HubSpot.OuTooltip, "OuTooltip");

export function OuTable<T>(props: Outreach.OuTableProps<T>): JSX.Element {
  return useCrm() === "hubspot" ? <HubSpot.OuTable<T> {...props} /> : <Outreach.OuTable<T> {...props} />;
}
